import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatPrice } from '../../lib/api';
import { Link } from 'react-router-dom';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (price, isRent) => {
  const color = isRent ? '#2E8B57' : '#0F4C81';
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${color};
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        font-family: 'Manrope', sans-serif;
      ">
        ${new Intl.NumberFormat('fr-DZ', { notation: 'compact' }).format(price)}
      </div>
    `,
    iconSize: [80, 24],
    iconAnchor: [40, 24],
    popupAnchor: [0, -24],
  });
};

// Map bounds updater
const MapBoundsUpdater = ({ properties }) => {
  const map = useMap();

  useEffect(() => {
    if (properties.length > 0) {
      const validProperties = properties.filter(p => p.latitude && p.longitude);
      if (validProperties.length > 0) {
        const bounds = L.latLngBounds(
          validProperties.map(p => [p.latitude, p.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }
    }
  }, [properties, map]);

  return null;
};

export const PropertyMap = ({ 
  properties = [], 
  center = [36.7538, 3.0588], 
  zoom = 10,
  height = '100%',
  showPopups = true 
}) => {
  const mapRef = useRef(null);

  const validProperties = properties.filter(p => p.latitude && p.longitude);

  return (
    <div style={{ height, width: '100%' }} data-testid="property-map">
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {validProperties.length > 0 && <MapBoundsUpdater properties={validProperties} />}
        
        {validProperties.map((property) => (
          <Marker
            key={property.id}
            position={[property.latitude, property.longitude]}
            icon={createCustomIcon(property.price, property.transaction_type === 'location')}
          >
            {showPopups && (
              <Popup>
                <Link to={`/property/${property.id}`} className="block w-48">
                  <img
                    src={property.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400'}
                    alt={property.title}
                    className="w-full h-24 object-cover rounded-lg mb-2"
                  />
                  <h4 className="font-semibold text-sm text-slate-800 line-clamp-1">
                    {property.title}
                  </h4>
                  <p className="text-primary-500 font-bold text-sm mt-1">
                    {formatPrice(property.price, property.transaction_type === 'location')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {property.neighborhood}, {property.city}
                  </p>
                </Link>
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

// Single property map
export const SinglePropertyMap = ({ property, height = '300px' }) => {
  if (!property?.latitude || !property?.longitude) {
    return (
      <div 
        style={{ height }} 
        className="bg-slate-100 rounded-lg flex items-center justify-center text-slate-500"
      >
        Localisation non disponible
      </div>
    );
  }

  return (
    <div style={{ height, width: '100%' }} className="rounded-lg overflow-hidden" data-testid="single-property-map">
      <MapContainer
        center={[property.latitude, property.longitude]}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[property.latitude, property.longitude]} />
      </MapContainer>
    </div>
  );
};
